import { FC, useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Stack, Text, Button, Icon, Alert, AlertIcon } from '@chakra-ui/react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { EventType, EventMessage, AuthError } from '@azure/msal-browser';
import { SiMicrosoft } from 'react-icons/si';
import HeaderComponent from '@components/HeaderComponent/HeaderComponent';
import FooterComponent from '@components/FooterComponent/FooterComponent';
import BackgroundImage from '@resources/images/home-banner.jpg';
import palette from '@theme/assets/palette';
import { AppRoutes } from '@routes/appRoutes';
import { loginRequest } from '../../auth/msalConfig';

const LoginPage: FC = () => {
    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const callbackId = instance.addEventCallback((event: EventMessage) => {
            if (event.eventType === EventType.LOGIN_FAILURE) {
                const error = event.error as AuthError | null;
                setErrorMessage(error?.errorMessage || 'Accesso non riuscito. Riprova.');
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance]);

    const handleLogin = useCallback(() => {
        setErrorMessage('');
        instance.loginRedirect(loginRequest).catch((error: AuthError) => {
            setErrorMessage(error?.errorMessage || 'Accesso non riuscito. Riprova.');
        });
    }, [instance]);

    if (isAuthenticated) {
        return <Navigate to={AppRoutes.HOME_PAGE} replace />;
    }

    return (
        <Stack minHeight="100vh" gap="0">
            <HeaderComponent
                title="Accedi"
                subtitle="Accedi con il tuo account Microsoft per continuare"
                backgroundImg={BackgroundImage}
                showUser={false}
            />
            <Stack
                direction="row"
                justify="center"
                align="flex-start"
                spacing="0px"
                alignSelf="stretch"
                background="gray.50"
                flex="1"
            >
                <Stack
                    paddingX="100px"
                    paddingTop="30px"
                    paddingBottom="90px"
                    justify="center"
                    align="center"
                    spacing="20px"
                    flex="1"
                    background="white"
                    boxShadow="xl"
                    alignSelf="stretch"
                    maxWidth="100%"
                >
                    <Stack
                        paddingTop="30px"
                        paddingBottom="20px"
                        justify="center"
                        align="center"
                        spacing="10px"
                        alignSelf="stretch"
                    >
                        <Text variant="titleL" color={palette.ui02} alignSelf="stretch" textAlign="center">
                            Anagrafica clienti/fornitori
                        </Text>
                        <Text variant="subtitle" color={palette.ui02} alignSelf="stretch" textAlign="center">
                            Effettua l&apos;accesso con il tuo account aziendale Microsoft
                        </Text>
                    </Stack>
                    {errorMessage ? (
                        <Alert status="error" borderRadius="md" maxWidth="533px">
                            <AlertIcon />
                            {errorMessage}
                        </Alert>
                    ) : null}
                    <Button leftIcon={<Icon as={SiMicrosoft} />} size="lg" variant="main" onClick={handleLogin}>
                        Accedi con Microsoft
                    </Button>
                </Stack>
            </Stack>
            <FooterComponent />
        </Stack>
    );
};

export default LoginPage;
